import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  inject,
} from '@angular/core';
import {
  ICliente,
  IConfigReenvio,
  IFilter,
  IPopulate,
  IQueryParam,
} from 'modelos/src';
import { Observable, firstValueFrom } from 'rxjs';
import { HelperService } from '../../../../../../auxiliares/servicios/helper.service';
import { Lista2Service } from '../../../../../../auxiliares/servicios/lista2.service';
import { FormularioDialogService } from '../../../../../../auxiliares/servicios/formulario-dialog.service';
import {
  configReenvioVigente,
  IReenviosTrackerData,
  ReenviosTrackerDialogComponent,
} from '../../../trackers/reenvios-tracker-dialog/reenvios-tracker-dialog.component';

/** Una cantidad del resumen (clientes, usuarios, equipos de cada módulo). */
interface Conteo {
  titulo: string;
  valor: string | null;
}

/** Reenvíos que involucran al cliente, ya resumidos. */
interface VistaReenvios {
  activas: string;
  alcance: string[];
  metodos?: string;
  heredadas?: number;
  total: number;
}

@Component({
  selector: 'app-detalles-cliente-info',
  templateUrl: './detalles-cliente-info.component.html',
  styleUrl: './detalles-cliente-info.component.scss',
  standalone: false,
})
export class DetallesClienteInfoComponent implements OnChanges {
  helper = inject(HelperService);
  lista2 = inject(Lista2Service);
  private formularios = inject(FormularioDialogService);

  @Input() public cliente?: ICliente;

  public conteos: Conteo[] = [];
  public modulos: string[] = [];
  public fechaCreacion?: string;
  public reenvios?: VistaReenvios;
  private idPedido?: string;

  /// Reenvíos que involucran a este cliente
  private configsReenvio: IConfigReenvio[] = [];
  public puedeVerReenvios = this.helper.tienePermiso(
    'Administración - Ver configuraciones de reenvíos',
  );

  /** Las cantidades cuentan también lo de los clientes hijos. */
  public includeChildrenInCounts = true;

  private getModulosActivos(): string[] {
    const config = this.cliente?.config;
    if (!config) return [];
    const modulos: [string, boolean][] = [
      ['Administración', !!config.moduloAdministracion?.activo],
      ['Vehículos', !!config.moduloVehiculos?.activo],
      ['Activos', !!config.moduloActivos?.activo],
      ['Alarmas Domiciliarias', !!config.moduloAlarmasDomiciliarias?.activo],
      ['Cámaras', !!config.moduloCamaras?.activo],
      ['Colectivos', !!config.moduloColectivos?.activo],
      ['Dispositivos LoRaWAN', !!config.moduloDispositivosLorawan?.activo],
      ['Luminarias', !!config.moduloLuminarias?.activo],
      ['Sirenas', !!config.moduloSirenas?.activo],
      [
        'Emergencias Médicas',
        !!config.moduloEmergencias?.activoEmergenciasMedicas,
      ],
      ['Botón BLE', !!config.moduloBotonBLE?.activo],
      ['Servicio Técnico', !!config.moduloEventosTecnicos?.activo],
      ['Logs', !!config.moduloLogs?.activo],
      ['Twilio', !!config.moduloTwilio?.activo],
      ['Integraciones', !!config.modulosIntegraciones?.activo],
    ];
    return modulos.filter(([, activo]) => activo).map(([nombre]) => nombre);
  }

  // ── Reenvíos ───────────────────────────────────────────────────────────────

  /// Involucran al cliente: las que él es dueño, las que reenvían sus entidades
  /// y las de un ancestro que además reenvía a sus clientes hijos.
  private async listarConfigsReenvio(): Promise<void> {
    const id = this.cliente?._id;
    if (!this.puedeVerReenvios || !id) return;
    const condiciones: IFilter<IConfigReenvio>[] = [
      { idCliente: id },
      { idClienteReenvio: id },
    ];
    const ancestros = this.cliente?.idsAncestros ?? [];
    if (ancestros.length) {
      condiciones.push({
        idClienteReenvio: { $in: ancestros },
        reenviarHijos: true,
      });
    }
    const populate: IPopulate[] = [
      { path: 'clienteReenvio', select: 'nombre' },
      { path: 'tracker', select: 'nombre' },
      { path: 'dispositivoAlarma', select: 'nombre' },
    ];
    const query: IQueryParam = {
      filter: JSON.stringify({ $or: condiciones }),
      populate: JSON.stringify(populate),
      includeChildren: true,
      sort: '-fechaCreacion',
    };
    try {
      const data = await firstValueFrom(this.lista2.configReenvios(query));
      this.configsReenvio = data.datos;
      this.armarReenvios();
    } catch (error) {
      console.error('Error al listar configuraciones de reenvío', error);
    }
  }

  private armarReenvios(): void {
    const vigentes = this.configsReenvio.filter((c) => configReenvioVigente(c));
    // Alcance: cómo se agrupan las vigentes
    const porAgrupacion = new Map<string, number>();
    for (const c of vigentes) {
      const clave = c.agrupacionReenvio ?? 'Sin agrupación';
      porAgrupacion.set(clave, (porAgrupacion.get(clave) ?? 0) + 1);
    }
    const metodos = [
      ...new Set(
        vigentes
          .map((c) => c.opcionesReenvio?.metodo)
          .filter((m): m is NonNullable<typeof m> => !!m),
      ),
    ];
    // Heredadas de un ancestro (el cliente no es dueño ni destino directo)
    const heredadas = vigentes.filter(
      (c) =>
        c.reenviarHijos &&
        c.idCliente !== this.cliente?._id &&
        c.idClienteReenvio !== this.cliente?._id,
    ).length;
    this.reenvios = {
      activas: vigentes.length
        ? `${vigentes.length} activa${vigentes.length !== 1 ? 's' : ''}`
        : 'No hay activas',
      alcance: [...porAgrupacion].map(
        ([agrupacion, cant]) => `${cant} · ${agrupacion}`,
      ),
      metodos: metodos.length ? metodos.join(', ') : undefined,
      heredadas: heredadas || undefined,
      total: this.configsReenvio.length,
    };
  }

  public verReenvios(): void {
    this.formularios.abrir<
      ReenviosTrackerDialogComponent,
      IReenviosTrackerData
    >(ReenviosTrackerDialogComponent, {
      tamano: 'mediano',
      cerrarAfuera: true,
      data: {
        nombreEntidad: this.cliente?.nombre ?? undefined,
        configs: this.configsReenvio,
      },
    });
  }

  // ── Cantidades ─────────────────────────────────────────────────────────────

  private buildQuery(): IQueryParam {
    const id = this.cliente!._id;
    const filter: Record<string, unknown> = this.includeChildrenInCounts
      ? { $or: [{ idsAncestros: id }, { idCliente: id }] }
      : { idCliente: id };
    return {
      filter: JSON.stringify(filter),
      includeChildren: this.includeChildrenInCounts,
    };
  }

  private buildClientes(): IQueryParam {
    const id = this.cliente!._id;
    const filter = this.includeChildrenInCounts
      ? { idsAncestros: id }
      : { idPadre: id };
    return {
      filter: JSON.stringify(filter),
      includeChildren: this.includeChildrenInCounts,
    };
  }

  private buildUsuarios(): IQueryParam {
    const id = this.cliente!._id;
    const filter: Record<string, unknown> = this.includeChildrenInCounts
      ? {
          $or: [{ 'permisos.idsAncestros': id }, { 'permisos.idCliente': id }],
        }
      : { 'permisos.idCliente': id };
    return {
      filter: JSON.stringify(filter),
      includeChildren: this.includeChildrenInCounts,
    };
  }

  /**
   * Cuántos clientes, usuarios y equipos de cada módulo activo tiene (con los
   * de los clientes hijos). Cada cantidad llega por su lado: hasta tenerla no
   * se muestra un número (ni un 0 que sería mentira).
   */
  private async cargarConteos(): Promise<void> {
    const cfg = this.cliente?.config;
    if (!this.cliente?._id) return;
    const pedidos: [string, () => Observable<{ totalCount: number }>][] = [];
    if (this.cliente.tipoCliente !== 'Final') {
      pedidos.push([
        'Clientes',
        () => this.lista2.countClientes(this.buildClientes()),
      ]);
    }
    pedidos.push([
      'Usuarios',
      () => this.lista2.countUsuarios(this.buildUsuarios()),
    ]);
    const q = () => this.buildQuery();
    const porModulo: [
      boolean | undefined,
      string,
      () => Observable<{ totalCount: number }>,
    ][] = [
      [
        cfg?.moduloAlarmasDomiciliarias?.activo,
        'Alarmas',
        () => this.lista2.countAlarmas(q()),
      ],
      [
        cfg?.moduloVehiculos?.activo,
        'Vehículos',
        () => this.lista2.countVehiculos(q()),
      ],
      [
        cfg?.moduloActivos?.activo,
        'Activos',
        () => this.lista2.countActivos(q()),
      ],
      [
        cfg?.moduloColectivos?.activo,
        'Colectivos',
        () => this.lista2.countColectivos(q()),
      ],
      [
        cfg?.moduloCamaras?.activo,
        'Cámaras',
        () => this.lista2.countCamaras(q()),
      ],
      [
        cfg?.moduloLuminarias?.activo,
        'Luminarias',
        () => this.lista2.countLuminarias(q()),
      ],
      [
        cfg?.moduloSirenas?.activo,
        'Sirenas',
        () => this.lista2.countSirenas(q()),
      ],
      [
        cfg?.moduloAdministracion?.activoTrackers,
        'Trackers',
        () => this.lista2.countTrackers(q()),
      ],
      [
        cfg?.moduloBotonBLE?.activo,
        'Botones BLE',
        () => this.lista2.countBotonesBLE(q()),
      ],
    ];
    for (const [activo, titulo, pedido] of porModulo) {
      if (activo) pedidos.push([titulo, pedido]);
    }

    this.conteos = pedidos.map(([titulo]) => ({ titulo, valor: '…' }));
    await Promise.allSettled(
      pedidos.map(async ([, pedido], i) => {
        try {
          const r = await firstValueFrom(pedido());
          this.conteos[i].valor = r.totalCount.toLocaleString('es-AR');
        } catch {
          this.conteos[i].valor = null;
        }
      }),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['cliente'] || !this.cliente) return;
    this.modulos = this.getModulosActivos();
    this.fechaCreacion = this.cliente.fechaCreacion
      ? HelperService.getFecha(this.cliente.fechaCreacion)
      : undefined;
    // Cantidades y reenvíos: una vez por cliente
    if (this.cliente._id !== this.idPedido) {
      this.idPedido = this.cliente._id;
      this.conteos = [];
      this.reenvios = undefined;
      this.configsReenvio = [];
      void this.cargarConteos();
      void this.listarConfigsReenvio();
    }
  }
}
